import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Code, Link, Text, Title, textVariantClasses } from '@/components/typography';
import { type TextVariantType } from '@/components/typography/types';
import { cn } from '@/lib/utils';

type Props = {
  children: string;
  externalLinks?: boolean;
  variant?: TextVariantType;
};

const Markdown = ({ children, externalLinks = false, variant = 'body1' }: Props) => {
  const renderLink = ({ children, href }: { children?: ReactNode; href?: string }) => (
    <Link
      className="text-[length:inherit]! leading-[inherit]! tracking-[inherit]!"
      showExternalIcon={externalLinks}
      target={externalLinks ? '_blank' : undefined}
      to={href}
      variant={variant}
    >
      {children}
    </Link>
  );

  return (
    <div
      className={cn(
        'markdown',
        variant === 'caption1' && 'text-xs leading-4 text-gray-700',
        variant === 'label' && textVariantClasses.label,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: renderLink,
          ...(variant === 'caption1'
            ? {
                p: ({ children }) => (
                  <Text className="text-gray-700" variant="caption1">
                    {children}
                  </Text>
                ),
              }
            : {}),
          body: ({ children }) => <Text variant="body1">{children}</Text>,
          code: ({ children, className }) => {
            const match = /language-(\w+)/.exec(className || '');
            const lang = className != null ? match?.[1] || 'text' : undefined;
            return <Code language={lang === 'markup' ? 'html' : lang}>{children}</Code>;
          },
          h1: ({ children }) => <Title level={1}>{children}</Title>,
          h2: ({ children }) => <Title level={2}>{children}</Title>,
          h3: ({ children }) => <Title level={3}>{children}</Title>,
          h4: ({ children }) => <Title level={4}>{children}</Title>,
          h5: ({ children }) => <Title level={5}>{children}</Title>,
          h6: ({ children }) => <Title level={6}>{children}</Title>,
          i: ({ children }) => <span className="italic">{children}</span>,
          link: renderLink,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default Markdown;
